import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Sparkle } from "../components/site/Decor";

const field = "w-full rounded-xl border border-[#EBD08A] bg-white px-4 py-3.5 text-base outline-none focus:border-[#FF1F8E] focus:shadow-[0_0_0_3px_rgba(255,31,142,.15)]";

export default function CustomerAuth() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next")?.startsWith("/") ? params.get("next") : "/account";
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = mode === "signup" ? await register(form.name, form.email, form.password) : await login(form.email, form.password);
      nav(u.role === "admin" ? "/admin" : next);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const tab = (m, label) => (
    <button type="button" onClick={() => { setMode(m); setError(""); }} data-testid={`auth-tab-${m}`}
      className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors ${mode === m ? "bg-[#0E0B0D] text-white" : "text-[#0E0B0D]/60"}`}>{label}</button>
  );

  return (
    <section className="bg-[#FFF4F8] px-4 py-16 sm:py-20">
      <form onSubmit={submit} className="stitch-box relative mx-auto max-w-md rounded-[2rem] bg-white p-8 shadow-[0_30px_80px_-40px_rgba(255,31,142,.35)] sm:p-10" data-testid="customer-auth-form">
        <Sparkle className="absolute right-7 top-7" />
        <p className="font-script text-4xl text-[#FF1F8E]">{mode === "signup" ? "Welcome" : "Welcome back"}</p>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">{mode === "signup" ? "Create your account" : "Sign in to your account"}</h1>
        <p className="mt-2 text-sm text-[#0E0B0D]/60">Track your custom order requests and see their status anytime.</p>
        <div className="mt-6 flex gap-1 rounded-full bg-[#FFF4F8] p-1">{tab("signin", "Sign in")}{tab("signup", "Create account")}</div>
        <div className="mt-6 space-y-4">
          {mode === "signup" && <input required placeholder="Your name" autoComplete="name" className={field} value={form.name} onChange={set("name")} data-testid="auth-name-input" />}
          <input required type="email" placeholder="Email" autoComplete="email" className={field} value={form.email} onChange={set("email")} data-testid="auth-email-input" />
          <input required type="password" minLength={mode === "signup" ? 8 : undefined} placeholder={mode === "signup" ? "Password (at least 8 characters)" : "Password"}
            autoComplete={mode === "signup" ? "new-password" : "current-password"} className={field} value={form.password} onChange={set("password")} data-testid="auth-password-input" />
          {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
          <button disabled={busy} className="btn-pink w-full disabled:opacity-60" data-testid="auth-submit-btn">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </div>
        <p className="mt-6 text-center text-xs text-[#0E0B0D]/50">No account needed to order — <Link to="/order" className="font-semibold text-[#FF1F8E]">send a request as a guest</Link>.</p>
      </form>
    </section>
  );
}
